/**
 * V2App — the redesign, ported 1:1 from vivideo-webapp-redesign.
 *
 * Rendered by App.tsx ONLY when canUseV2 (DEV build + allowlisted email) and
 * uiVersion === 'v2'. Routes mirror the redesign's App.tsx exactly; the shared
 * BrowserRouter lives in App.tsx (the redesign's main.tsx provided it there).
 */
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
// Side-effect: registers the 'v2' i18n namespace on the shared i18next instance.
import '@/v2/i18n';
import { Layout } from '@/v2/components/Layout';
import { HomePage } from '@/v2/pages/HomePage';
import { ProjectsPage } from '@/v2/pages/ProjectsPage';
import { AssetsPage } from '@/v2/pages/AssetsPage';
import { IntegrationsPage } from '@/v2/pages/IntegrationsPage';
import { AutomationsPage } from '@/v2/pages/AutomationsPage';
import { TemplatesPage } from '@/v2/pages/TemplatesPage';
import { DevelopersPage } from '@/v2/pages/DevelopersPage';
import { UpgradePage } from '@/v2/pages/UpgradePage';
import { AccountPage } from '@/v2/pages/AccountPage';
import { ApiKeysPage } from '@/v2/pages/ApiKeysPage';
import { TeamPage } from '@/v2/pages/TeamPage';
import { JoinTeamPage } from '@/v2/pages/JoinTeamPage';
import { AgentProjectPage } from '@/v2/pages/AgentProjectPage';
import { AutoPreviewPage } from '@/v2/pages/AutoPreviewPage';
import { UpgradeModal } from '@/v2/components/UpgradeModal';
import { ExitOffer } from '@/features/subscription/ExitOffer';
import { RewardsPage } from '@/features/rewards';

// Remount AgentProjectPage when the project id changes (esp. new → real id) so
// its internal state starts clean instead of carrying over the create-mode flags.
function KeyedAgentProject() {
  const { id } = useParams<{ id: string }>();
  return <AgentProjectPage key={id} />;
}

// Legacy alias: the upgrade page now lives at /subscription (V1 parity), but
// keep redirecting the old /upgrade slug — preserving query params (Stripe
// returns, ?context=…) — so in-flight links and bookmarks still resolve.
function UpgradeRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/subscription${search}`} replace />;
}

export function V2App() {
  // V2 stays left-to-right for every language (Arabic/Hebrew included) — we
  // translate text only, not layout. See the note in src/v2/i18n/index.ts.
  return (
    <>
    {/* Global free-tier paywall popup (creation caps) — pops in place anywhere. */}
    <UpgradeModal />
    {/* Recovery/exit offer — overlay on leaving the paywall, then a persistent
        banner with the real server countdown until it expires. Renders nothing
        unless an offer is live. */}
    <ExitOffer />
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/automations" element={<AutomationsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        {/* Legacy slug — Templates v2 (Presets) merged into /templates. */}
        <Route path="/templates-v2" element={<Navigate to="/templates" replace />} />
        <Route path="/developers" element={<DevelopersPage />} />
        {/* Legacy slug — the MCP Skills teaser is now the Developers hub. */}
        <Route path="/mcp" element={<Navigate to="/developers" replace />} />
        {/* Canonical upgrade slug — matches V1 so analytics ("Page Viewed"),
            ad pixels and external links all see the same /subscription path. */}
        <Route path="/subscription" element={<UpgradePage />} />
        <Route path="/upgrade" element={<UpgradeRedirect />} />
        <Route path="/account" element={<AccountPage />} />
        {/* PUBLIC-API key management (Settings → More → API Keys). */}
        <Route path="/account/api-keys" element={<ApiKeysPage />} />
        <Route path="/rewards" element={<RewardsPage />} />
        {/* Team workspaces: the team hub + the invite-link landing (behind the
            app's auth gate, so invitees sign in first and land back here). */}
        <Route path="/team" element={<TeamPage />} />
        <Route path="/team/join/:token" element={<JoinTeamPage />} />
      </Route>
      {/* Focused full-screen view, outside the app shell. AgentProjectPage hosts
          BOTH the new agent chat (Vivideo planner) and manual fal projects.
          Keyed by :id so /projects/new → /projects/:realId remounts with fresh
          state once the first message creates the project. */}
      <Route path="/projects/:id" element={<KeyedAgentProject />} />
      {/* Free-tier Auto-generate preview — project-page-style funnel (loader → ready
          gate → paywall), reached from Home so it never feels like a Home pop-up. */}
      <Route path="/auto-preview" element={<AutoPreviewPage />} />
      {/* V1-only paths (e.g. /files, /subscription) fall back to V2 home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
